// Copyright (c) StrangeBee 2023

import React, { ReactNode, useCallback, useState, ReactElement } from 'react';
import { ButtonProps } from 'antd';

interface ChildrenProps<T> {
    data: T;
    next: () => void;
    isLast: boolean;
    finish: () => void;
}

interface PoolDrawerProps<T> {
    triggerElem: ReactElement;
    children?: ({ data, next }: ChildrenProps<T>) => ReactNode;
    poolData: T[];
    onStart?: () => void;
    onFinish?: () => void;
}

export const PoolDrawer = <T,>({
    children,
    poolData,
    triggerElem,
    onStart,
    onFinish,
}: PoolDrawerProps<T>): ReactElement => {
    const [index, setIndex] = useState(0);
    const [isOpen, setIsOpen] = useState(false);

    const next = useCallback(() => {
        setIndex((index) => {
            const newIndex = (index + 1) % poolData.length;
            if (newIndex === poolData.length - 1) {
                onFinish?.();
            }
            if (index > newIndex) {
                setIsOpen(false);
            }
            return newIndex;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, poolData]);

    const toggleDrawer = () => {
        onStart?.();
        setIsOpen(true);
    };

    const onFinishHandler = () => {
        setIsOpen(false);
        setIndex(0);
        onFinish?.();
    };

    return (
        <>
            {React.cloneElement<ButtonProps>(triggerElem, {
                onClick: toggleDrawer,
            })}
            {isOpen &&
                index < poolData.length &&
                children?.({
                    data: poolData[index],
                    next,
                    isLast: index === poolData.length - 1,
                    finish: onFinishHandler,
                })}
        </>
    );
};
